import React from 'react';
import { PenLine, Search } from 'lucide-react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { MultiCombobox } from '../../ui/multi-combobox';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../../ui/select';
import {
    REPORT_SORT_OPTIONS,
    ReportSortValue,
} from '../../../services/reportService';

const CPT_CODE_OPTIONS = ['76014', '76015', '76016', '76018', '76019'];

interface ManageReportsFiltersProps {
    searchTerm: string;
    onSearchChange: (value: string) => void;
    sort: ReportSortValue;
    onSortChange: (value: ReportSortValue) => void;
    cptCodes: string[];
    onCptCodesChange: (codes: string[]) => void;
    needsSignatureOnly: boolean;
    onNeedsSignatureChange: (value: boolean) => void;
}

const ManageReportsFilters: React.FC<ManageReportsFiltersProps> = ({
    searchTerm,
    onSearchChange,
    sort,
    onSortChange,
    cptCodes,
    onCptCodesChange,
    needsSignatureOnly,
    onNeedsSignatureChange,
}) => {
    return (
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:gap-4">
            <div className="relative w-full sm:max-w-md">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                    placeholder="Search by case ID, author, exam, indication, or scan date"
                    value={searchTerm}
                    onChange={(event) => onSearchChange(event.target.value)}
                    className="pl-9"
                />
            </div>
            <Select
                value={sort}
                onValueChange={(value) =>
                    onSortChange(value as ReportSortValue)
                }
            >
                <SelectTrigger
                    aria-label="Sort cases"
                    className="w-full sm:w-56"
                >
                    <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                    {REPORT_SORT_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                            {option.label}
                        </SelectItem>
                    ))}
                </SelectContent>
            </Select>
            <MultiCombobox
                options={CPT_CODE_OPTIONS}
                value={cptCodes}
                onChange={onCptCodesChange}
                placeholder="Filter by CPT code"
                searchPlaceholder="Search CPT codes"
                emptyText="No matching CPT codes"
                className="w-full sm:w-56"
                aria-label="Filter by CPT code"
            />
            <Button
                variant={needsSignatureOnly ? 'default' : 'outline'}
                aria-pressed={needsSignatureOnly}
                onClick={() => onNeedsSignatureChange(!needsSignatureOnly)}
                className="w-full gap-2 sm:w-auto"
            >
                <PenLine className="h-4 w-4" />
                Needs my signature
            </Button>
        </div>
    );
};

export default ManageReportsFilters;
